import PropTypes from 'prop-types';
import { createContext, useMemo } from 'react';
import { SnackbarProvider, useSnackbar } from 'notistack';
import styled from 'styled-components';

const StyledSnackbarProvider = styled(SnackbarProvider)`
  font-family: AvenirNextLTPro-Regular, arial, helvetica, verdana, sans-serif;
`;

/**
 * The ToastContext wraps the components from _app.js, and is just a context provider for notistack.
 * Features of the createToast function which is stored in the context includes using colored variants,
 * icons in the toasts, monitoring toast duplicates, anchoring toasts, and more. The first parameter of
 * createToast is the message, and the second is the options, the most common usecase is the variant, which
 * gives 'success', 'error', 'info', 'warning' and 'default' colors.
 * Full documentation for usage can be found here: https://iamhosseindhv.com/notistack
 * To use the toast, import ToastContext into your component file, then consume the context in the
 * component with const { createToast } = useContext(ToastContext)
 */
export const ToastContext = createContext();

function ToastProvider({ children }) {
  const { enqueueSnackbar } = useSnackbar();

  // Memoize the value object
  const contextValue = useMemo(() => ({ createToast: enqueueSnackbar }), [enqueueSnackbar]);

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>;
}

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired
};

function ToastWrapper({ children }) {
  return <StyledSnackbarProvider>
    <ToastProvider>{children}</ToastProvider>
  </StyledSnackbarProvider>
}

ToastWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

export default ToastWrapper;
